import { PostOfferCardBG } from 'app/design/images/post-offer-card-bg';
import { Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Text } from 'app/design/primitives/text';

import { useDeviceWidth } from 'app/hooks/use-device-size';
import { useUISize } from 'app/utils/web-helpers';
import type { ReactNode } from 'react';
import type { ViewProps } from 'react-native';
import { StyleSheet, View } from 'react-native';
import { createTheme as createDSTheme } from 'app/shopify';
import type { Theme } from 'app/shopify';
import { IconPlus, IconSpeakerphone } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import {
  listingPromptBannerSharedInnards,
  POST_OFFER_CARD_ART_ASPECT_RATIO,
  PROMPT_BANNER_MAX_WIDTH,
  promptBannerPrimaryButtonStyle,
  promptBannerScreenHorizontalGutter,
} from './prompt-banner-card.styles';

export {
  listingPromptBannerSharedInnards,
  MINT_LISTING_PROMPT_BACKGROUND,
  mintListingPromptBannerLayout,
  mintListingPromptBodyTextProps,
  mintListingPromptTitleTextProps,
  POST_OFFER_CARD_ART_ASPECT_RATIO,
  PROMPT_BANNER_MAX_WIDTH,
  promptBannerPillButtonStyle,
  promptBannerPrimaryButtonStyle,
  promptBannerScreenHorizontalGutter,
  promptBannerV2NeedButtonStyle,
} from './prompt-banner-card.styles';

const theme = createDSTheme();

type SpacingKey = keyof Theme['spacing'];

export type PromptBannerCardProps = ViewProps & {
  backgroundColor: string;
  children: ReactNode;
  backgroundDecoration?: ReactNode;
  contentGap?: SpacingKey;
  contentPaddingHorizontal?: SpacingKey;
  contentPaddingTop?: SpacingKey;
  contentPaddingVertical?: SpacingKey;
};

function PromptBannerCard({
  backgroundColor,
  backgroundDecoration,
  children,
  contentGap = 'spacing.general.x4',
  contentPaddingHorizontal = 'spacing.general.x6',
  contentPaddingTop,
  contentPaddingVertical = 'spacing.general.x6',
  style,
  ...rest
}: PromptBannerCardProps) {
  const { isSmallUI } = useUISize();

  return (
    <View
      {...rest}
      style={[
        isSmallUI
          ? {
              width: '100%',
              alignSelf: 'stretch',
              borderRadius: theme.borderRadii['radius.lg'],
              overflow: 'hidden',
              backgroundColor,
            }
          : {
              width: '100%',
              alignSelf: 'flex-start',
              borderRadius: theme.borderRadii['radius.lg'],
              overflow: 'hidden',
              backgroundColor,
            },
        style,
      ]}
    >
      {backgroundDecoration ? (
        <View pointerEvents="none" style={[StyleSheet.absoluteFill, { overflow: 'hidden' }]}>
          {backgroundDecoration}
        </View>
      ) : null}
      <Column
        alignItems="flex-start"
        gap={contentGap}
        paddingHorizontal={contentPaddingHorizontal}
        paddingBottom={contentPaddingVertical}
        paddingTop={contentPaddingTop ?? contentPaddingVertical}
        position="relative"
        zIndex={1}
      >
        {children}
      </Column>
    </View>
  );
}

/** SVG pattern layer for mint offer / invite banners. */
export function MintListingPromptPattern() {
  return (
    <View style={{ bottom: 0, left: 0, position: 'absolute', right: 0, top: 0 }}>
      <View style={{ aspectRatio: POST_OFFER_CARD_ART_ASPECT_RATIO, width: '100%' }}>
        <PostOfferCardBG style={{ height: '100%', width: '100%' }} />
      </View>
    </View>
  );
}

export function useMintListingPromptMinHeight(): number {
  return 155;
}

/** Capped width for large horizontal CTAs (e.g. post-need v2). */
export function usePromptBannerCappedContentWidth(): number {
  const dw = useDeviceWidth();
  return Math.min(PROMPT_BANNER_MAX_WIDTH, dw - 2 * promptBannerScreenHorizontalGutter);
}

export function usePromptBannerPrimaryButtonStyle() {
  const { isSmallUI, isLargeUI } = useUISize();
  return {
    ...promptBannerPrimaryButtonStyle,
    width: isSmallUI ? ('100%' as const) : undefined,
    alignSelf: isLargeUI ? ('center' as const) : undefined,
  };
}

export interface MegaphonePromptBannerProps {
  backgroundColor: string;
  foregroundColor: string;
  /** Fill color for the `+` icon on the primary button (matches card background for contrast). */
  plusIconColor: string;
  title: string;
  body: string;
  buttonLabel: string;
  onPress: () => void;
}

/** Megaphone + title, body, primary CTA — shared by add-address and post-need (non-v2). */
export function MegaphonePromptBanner({
  backgroundColor,
  foregroundColor,
  plusIconColor,
  title,
  body,
  buttonLabel,
  onPress,
}: MegaphonePromptBannerProps) {
  const minHeight = useMintListingPromptMinHeight();
  const primaryButtonStyle = usePromptBannerPrimaryButtonStyle();

  return (
    <PromptBannerCard {...listingPromptBannerSharedInnards} backgroundColor={backgroundColor} style={{ minHeight }}>
      <Row alignItems="center" gap="spacing.general.x2">
        <IconSpeakerphone strokeWidth={ICON_STROKE_WIDTH} size={30} color={foregroundColor} />
        <Text fontWeight="700" style={{ color: foregroundColor }} variant="bodyLarge">
          {title}
        </Text>
      </Row>
      <Text style={{ color: foregroundColor }} textAlign="left" variant="bodyBase">
        {body}
      </Text>
      <Button onPress={onPress} style={primaryButtonStyle}>
        <Row alignItems="center" gap="spacing.general.x2">
          <Text color="surface.default" variant="bodyBase">
            {buttonLabel}
          </Text>
          <IconPlus strokeWidth={ICON_STROKE_WIDTH} size={21} color={plusIconColor} />
        </Row>
      </Button>
    </PromptBannerCard>
  );
}

export { PromptBannerCard };
